/**
 * Fuentes de `sc-table`: local (todas las filas en el navegador, con `applyGridQuery`) o remota (la
 * API resuelve la `GridQuery` sobre el total y devuelve la página: `page`, `limit` y `q` en JSON).
 * Las dos responden lo mismo para la misma consulta (contrato de grilla).
 */
import { isSignal, type Signal } from '@angular/core';
import { defer, map, of, type Observable } from 'rxjs';
import { applyGridQuery } from './apply-grid-query';
import type { GridQuery, GridResult, GridSpec } from './grid-query';
import { GridQueryError, validateGridQuery } from './validate-grid-query';

/** Tope de filas por página de la API (`queryParser`): `limit` no puede pasar de aquí. */
export const MAX_REMOTE_PAGE_SIZE = 500;

export interface GridSource<T> {
  readonly mode: 'local' | 'remote';
  load(query: GridQuery): Observable<GridResult<T>>;
}

/** Respuesta de un listado de la API (`Response.success` con `meta` de `gridMeta`). */
export interface GridPage {
  data: unknown[];
  meta?: { total?: number } | null;
}

/** Lo que la fuente remota pide al endpoint. */
export interface GridRequest {
  page: number;
  limit: number;
  q: string;
}

/**
 * Todas las filas en el navegador. La consulta se valida con las reglas de la API (lo que daría 400
 * en remoto, falla igual aquí) y se resuelve al suscribirse, con las filas de ese momento.
 */
export function localSource<T>(
  rows: readonly T[] | Signal<readonly T[]>,
  spec: GridSpec,
): GridSource<T> {
  return {
    mode: 'local',
    load: (query) =>
      defer(() => {
        validateGridQuery(query, spec);
        return of(applyGridQuery(isSignal(rows) ? rows() : rows, query, spec));
      }),
  };
}

/** `q` del contrato: la consulta sin la paginación, que va en `page` y `limit`. */
export function gridQueryParam(query: GridQuery): string {
  const view = query.view ?? 'rows';
  return JSON.stringify({
    sort: query.sort,
    ...(query.search?.trim() ? { search: query.search.trim() } : {}),
    filters: query.filters,
    groupBy: query.groupBy,
    ...(query.groupPath?.length ? { groupPath: query.groupPath } : {}),
    view,
    ...(view === 'facet' && query.facetField ? { facetField: query.facetField } : {}),
  });
}

/**
 * La API resuelve la consulta. Si se pasa `spec`, la consulta se valida antes de pedirla (el mismo
 * error que daría la API, sin ir a ella). La página va de 1 a `MAX_REMOTE_PAGE_SIZE` filas: la API
 * no entrega más, y con `limit` 0 entregaría 20 creyendo que son todas.
 */
export function remoteSource<T>(
  fetch: (request: GridRequest) => Observable<GridPage>,
  spec?: GridSpec,
): GridSource<T> {
  return {
    mode: 'remote',
    load: (query) =>
      defer(() => {
        if (query.pageSize < 1 || query.pageSize > MAX_REMOTE_PAGE_SIZE)
          throw new GridQueryError(
            `El tamaño de página remoto va de 1 a ${MAX_REMOTE_PAGE_SIZE} filas`,
          );
        if (spec) validateGridQuery(query, spec);
        const view = query.view ?? 'rows';
        return fetch({ page: query.page, limit: query.pageSize, q: gridQueryParam(query) }).pipe(
          map(({ data, meta }): GridResult<T> => {
            const list = data ?? [];
            const total = meta?.total ?? list.length;
            if (view === 'groups')
              return { groups: list as { key: unknown; count: number }[], total };
            if (view === 'facet') return { values: list as { value: unknown; count: number }[] };
            return { rows: list as T[], total };
          }),
        );
      }),
  };
}
